import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PainelMonitoramentoEntregasHttp from '../../../../services/api/https/dashboard/painel-monitoramento-entregas.http'
import { getUsuarioInfo } from '../../../../services/localstorage/localstorage.service'
import { parseToMysqlFormat } from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCHING_DATA: 'Buscando dados do painel de monitoramento...',
}

const AUTO_REFRESH_MS = 60 * 1000

function getPeriodoHoje() {
  const hoje = new Date()
  const dataInicial = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0)
  const dataFinal = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59)
  return { dataInicial, dataFinal }
}

function createInitialState() {
  const { dataInicial, dataFinal } = getPeriodoHoje()
  return {
    pInicial: dataInicial,
    pFinal: dataFinal,
    loadingMessage: LOAD_MESSAGE.NONE,
    errorMessage: null,
    dados: null,
    analistas: [],
    selectedAnalista: null,
    canSelectAnalista: false,
    modalEntrega: null,
    modalRota: null,
    modalRetorno: null,
    modalDelegar: false,
    modalAssumir: false,
  }
}

export default function usePainelMonitoramentoEntregas(selectedTp) {
  const [state, setState] = useState(createInitialState)
  const intervalRef = useRef(null)
  const lastParamsRef = useRef(null)

  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const fetchDashboard = useCallback(
    async ({ pInicial, pFinal, analista, silent = false }) => {
      const dateValidationMessage = validatePeriodo(pInicial, pFinal)
      if (dateValidationMessage) {
        lastParamsRef.current = null
        setState(prev => ({
          ...prev,
          errorMessage: dateValidationMessage,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
        return
      }

      const { id: userId } = getUsuarioInfo()
      const analistaId = analista ? analista.analista_entrega_id : null

      if (!silent) {
        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.FETCHING_DATA }))
      }

      try {
        const data = await PainelMonitoramentoEntregasHttp.getDashboard(
          selectedTp || 0,
          userId,
          parseToMysqlFormat(pInicial),
          parseToMysqlFormat(pFinal),
          analistaId,
        )

        const selectedAnalista =
          data.analistas.find(
            item => item.analista_entrega_id === data.selected_analista.analista_entrega_id,
          ) || data.selected_analista

        lastParamsRef.current = {
          pInicial,
          pFinal,
          analista: selectedAnalista,
        }

        setState(prev => ({
          ...prev,
          pInicial,
          pFinal,
          dados: data,
          analistas: data.analistas,
          selectedAnalista,
          canSelectAnalista: data.can_select_analista,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      } catch (e) {
        lastParamsRef.current = null
        setState(prev => ({
          ...prev,
          dados: null,
          analistas: [],
          selectedAnalista: null,
          errorMessage: errorHandler(e),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      }
    },
    [selectedTp],
  )

  const startAutoRefresh = useCallback(() => {
    stopAutoRefresh()
    intervalRef.current = setInterval(() => {
      if (!lastParamsRef.current) return
      fetchDashboard({ ...lastParamsRef.current, silent: true })
    }, AUTO_REFRESH_MS)
  }, [fetchDashboard, stopAutoRefresh])

  useEffect(() => {
    lastParamsRef.current = null
    fetchDashboard({
      pInicial: state.pInicial,
      pFinal: state.pFinal,
      analista: null,
      silent: false,
    }).then(startAutoRefresh)

    return stopAutoRefresh
  }, [selectedTp])

  const localDispatch = useMemo(
    () => ({
      resetState() {
        setState(prev => ({
          ...prev,
          errorMessage: null,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      onPeriodoInicialChange(value) {
        setState(prev => ({ ...prev, pInicial: value }))
      },

      onPeriodoFinalChange(value) {
        setState(prev => ({ ...prev, pFinal: value }))
      },

      onAnalistaChange(value) {
        setState(prev => ({ ...prev, selectedAnalista: value }))
      },

      async onBuscar() {
        await fetchDashboard({
          pInicial: state.pInicial,
          pFinal: state.pFinal,
          analista: state.selectedAnalista,
          silent: false,
        })
        startAutoRefresh()
      },

      stopAutoRefresh,

      openEntregaModal(delivery) {
        setState(prev => ({ ...prev, modalEntrega: delivery }))
      },

      markOcorrenciasEntregaVistas(deliveryStepVehicleId) {
        setState(prev => {
          if (!prev.dados || !prev.dados.grupos) return prev

          const grupos = prev.dados.grupos.map(grupo => ({
            ...grupo,
            rotas: grupo.rotas.map(rota => ({
              ...rota,
              deliveries: rota.deliveries.map(delivery =>
                delivery.delivery_step_vehicle_id === deliveryStepVehicleId
                  ? { ...delivery, has_pending_ocorrencia: false }
                  : delivery,
              ),
            })),
          }))

          return { ...prev, dados: { ...prev.dados, grupos } }
        })
      },

      closeEntregaModal() {
        setState(prev => ({ ...prev, modalEntrega: null }))
      },

      openRotaModal(rota) {
        setState(prev => ({ ...prev, modalRota: rota }))
      },

      closeRotaModal() {
        setState(prev => ({ ...prev, modalRota: null }))
      },

      openRetornoModal(rota) {
        if (!rota.returned_time_raw) return
        setState(prev => ({ ...prev, modalRetorno: rota }))
      },

      closeRetornoModal() {
        setState(prev => ({ ...prev, modalRetorno: null }))
      },

      openDelegarModal() {
        // Pausa o auto-refresh do painel enquanto o modal de delegação está aberto
        // para não re-renderizar o formulário no meio do preenchimento
        stopAutoRefresh()
        setState(prev => ({ ...prev, modalDelegar: true }))
      },

      closeDelegarModal() {
        setState(prev => ({ ...prev, modalDelegar: false }))
        startAutoRefresh()
      },

      openAssumirModal() {
        // Mesmo comportamento do modal de delegação: pausa o auto-refresh
        stopAutoRefresh()
        setState(prev => ({ ...prev, modalAssumir: true }))
      },

      closeAssumirModal() {
        setState(prev => ({ ...prev, modalAssumir: false }))
        startAutoRefresh()
      },
    }),
    [
      fetchDashboard,
      startAutoRefresh,
      state.pFinal,
      state.pInicial,
      state.selectedAnalista,
      stopAutoRefresh,
    ],
  )

  return { state, localDispatch }
}

function validatePeriodo(pInicial, pFinal) {
  if (!pInicial || !pFinal) return 'Informe o Período Inicial e o Período Final'

  const start = new Date(pInicial)
  const end = new Date(pFinal)
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)

  if (end < start) return 'A Data Final deve ser maior ou igual a Data Inicial.'

  const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  if (diffDays > 31) return 'Período superior a 31 dias. Diminua o Período e tente novamente.'

  return null
}
